import axios from 'axios';

// const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://vendor-booking-api.test/api';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.vendalist.com/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // Enable upload progress tracking
  onUploadProgress: (progressEvent) => {
    // This will be overridden by individual requests if they provide their own
  },
});

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** Seconds to wait before retrying after HTTP 429 (caps avoid blocking the UI forever). */
function parseRetryAfterSeconds(response) {
  const header = response?.headers?.['retry-after'];
  if (header != null && header !== '') {
    const parsed = parseInt(String(header).trim(), 10);
    if (!Number.isNaN(parsed) && parsed >= 0) {
      return Math.min(Math.max(parsed, 1), 120);
    }
  }
  const raw = response?.data?.retry_after;
  if (raw != null && raw !== '') {
    const parsed = typeof raw === 'number' ? raw : parseInt(String(raw).trim(), 10);
    if (!Number.isNaN(parsed) && parsed >= 0) {
      return Math.min(Math.max(parsed, 1), 120);
    }
  }
  return 3;
}

function shouldRetry429Request(config) {
  if (!config) return false;
  const method = (config.method || 'get').toLowerCase();
  if (method === 'get' || method === 'head') return true;
  return config.retryOn429 === true;
}

function max429RetriesForRequest(config) {
  return typeof config.max429Retries === 'number' ? config.max429Retries : 2;
}

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token expiration and rate limiting
let sessionExpiredEventDispatched = false;

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const res = error.response;
    const config = error.config;

    // Respect Retry-After for transient bursts. Never auto-retry mutating POST/PUT/PATCH/DELETE
    // unless the caller sets config.retryOn429 = true (idempotent / safe endpoints only).
    if (res?.status === 429 && config && shouldRetry429Request(config)) {
      const attempt = config.__retry429Count || 0;
      const maxRetries = max429RetriesForRequest(config);
      if (attempt < maxRetries) {
        const delaySec = parseRetryAfterSeconds(res);
        const jitterMs = Math.floor(Math.random() * 400);
        const delayMs = Math.min(delaySec * 1000 + jitterMs, 60000);
        await sleep(delayMs);
        config.__retry429Count = attempt + 1;
        try {
          return await api.request(config);
        } catch (retryErr) {
          error = retryErr;
        }
      }
    }

    if (error.response?.status === 429) {
      error.retryAfterSeconds = parseRetryAfterSeconds(error.response);
      error.isRateLimited = true;
    }

    if (error.response?.status === 401) {
      // Token is invalid or expired
      // Don't dispatch multiple sessionExpired events
      const isLogoutRequest = error.config?.url?.includes('/auth/logout');
      
      // Skip handling if this is a logout request (logout handles its own cleanup)
      if (!isLogoutRequest && !sessionExpiredEventDispatched) {
        sessionExpiredEventDispatched = true;
        localStorage.removeItem('token');
        
        // Only redirect if not already on login/register page
        if (!window.location.pathname.includes('/login') && 
            !window.location.pathname.includes('/register')) {
          // Dispatch a custom event that components can listen to
          window.dispatchEvent(new CustomEvent('sessionExpired', {
            detail: { message: 'Your session has expired. Please login again.' }
          }));
        }
        
        // Reset flag after a delay
        setTimeout(() => {
          sessionExpiredEventDispatched = false;
        }, 2000);
      }
    } else if (
      error.response?.status === 403 &&
      (error.response?.data?.requires_subscription || error.response?.data?.subscription_expired)
    ) {
      window.dispatchEvent(
        new CustomEvent('vendorSubscriptionRequired', {
          detail: {
            message:
              error.response?.data?.message ||
              'Your subscription has ended. Please upgrade your plan.',
          },
        })
      );
    } else if (error.response?.status === 403 && error.response?.data?.message?.includes('Account is deactivated')) {
      // Vendor suspended or account deactivated - log them out immediately
      if (!sessionExpiredEventDispatched) {
        sessionExpiredEventDispatched = true;
        localStorage.removeItem('token');
        
        if (!window.location.pathname.includes('/login') && 
            !window.location.pathname.includes('/register')) {
          window.dispatchEvent(new CustomEvent('sessionExpired', {
            detail: { message: error.response?.data?.error || 'Your account has been suspended. Please contact support.' }
          }));
        }
        
        setTimeout(() => {
          sessionExpiredEventDispatched = false;
        }, 2000);
      }
    }
    return Promise.reject(error);
  }
);

// Cache for getCurrentUser to prevent multiple calls
let currentUserPromise = null;

export const authService = {
  // Login user
  login: async (credentials) => {
    const response = await api.post('/auth/login', credentials);
    return response.data; // Return the data directly
  },

  // Register user
  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    return response.data; // Return the data directly
  },

  // Get current user with caching to prevent multiple simultaneous calls
  getCurrentUser: async () => {
    if (currentUserPromise) {
      return currentUserPromise;
    }

    currentUserPromise = api
      .get('/auth/me')
      .then((response) => {
        currentUserPromise = null;
        return response.data.data;
      })
      .catch((err) => {
        currentUserPromise = null;
        throw err;
      });

    return currentUserPromise;
  },

  // Refresh token
  refreshToken: async () => {
    const response = await api.post('/auth/refresh');
    return response;
  },

  /** Permanently close account (soft-delete). Password omitted when user has no local password (e.g. Google-only). */
  deleteAccount: async ({ password, reason }) => {
    const payload = {};
    if (reason) payload.reason = reason;
    if (password != null && String(password).trim() !== '') {
      payload.password = password;
    }
    const response = await api.post('/user/profile/delete', payload);
    return response.data;
  },

  // Logout user
  logout: async () => {
    // Check if token exists before making the API call
    const token = localStorage.getItem('token');
    if (!token) {
      // No token, nothing to logout
      return { data: { success: true, message: 'Already logged out' } };
    }
    
    try {
      const response = await api.post('/auth/logout');
      return response;
    } catch (error) {
      // If it's a 401, the token is already invalid, so we can consider it logged out
      if (error.response?.status === 401) {
        return { data: { success: true, message: 'Token already invalid' } };
      }
      throw error;
    }
  },

  // Forgot password
  forgotPassword: async (email) => {
    const response = await api.post('/auth/forgot-password', { email });
    return response;
  },

  // Reset password
  resetPassword: async (token, password, passwordConfirmation, email) => {
    const response = await api.post('/auth/reset-password', { 
      token, 
      password, 
      password_confirmation: passwordConfirmation,
      email 
    });
    return response;
  },

  // Update profile
  updateProfile: async (userData) => {
    const response = await api.put('/auth/profile', userData);
    return response;
  },

  // Change password
  changePassword: async (passwordData) => {
    const response = await api.put('/auth/change-password', passwordData);
    return response;
  },

  // OAuth login/register
  oauthLogin: async (oauthData) => {
    const response = await api.post('/auth/oauth', oauthData);
    return response.data;
  },
};

export default api;
